/**
 * Listen for Basecamp todo refresh signals and reload work-spec related data.
 * Used by full viewer and todos-table preview pane.
 */

import { useEffect } from 'react';
import {
  BASECAMP_INGESTION_COMPLETE,
  BASECAMP_TODO_REFRESH,
} from '@/lib/basecamp-ingestion-events';
import { extractTodoIdFromPath } from '@/lib/todo-id-from-path';

type RefreshOptions = {
  todoId: string;
  onRefreshRelatedDocs: () => void;
  /** Called on manual toolbar refresh (basecamp-todo-refresh). */
  onFullRefresh?: () => void;
  /** Called when async ingestion completes; defaults to onRefreshRelatedDocs. */
  onIngestionComplete?: () => void;
};

/** Returns true when a refresh event targets this todo or is a global refresh. */
function eventMatchesTodo(eventTodoId: string | undefined, todoId: string): boolean {
  return !eventTodoId || eventTodoId === todoId;
}

/** Extracts a document path from document-created or document-moved event detail. */
function documentPathFromMutationDetail(detail: Record<string, unknown> | undefined): string | null {
  if (!detail) return null;
  const path = detail.newPath ?? detail.filePath ?? detail.path;
  return typeof path === 'string' ? path : null;
}

/** Subscribes to Basecamp refresh events and reloads work specs for the given todo. */
export function useBasecampTodoRefreshEvents({
  todoId,
  onRefreshRelatedDocs,
  onFullRefresh,
  onIngestionComplete,
}: RefreshOptions): void {
  useEffect(() => {
    const handleTodoRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ todoId?: string }>).detail;
      if (!eventMatchesTodo(detail?.todoId, todoId)) return;
      if (onFullRefresh) {
        onFullRefresh();
      } else {
        onRefreshRelatedDocs();
      }
    };

    const handleIngestionComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ todoId?: string }>).detail;
      if (detail?.todoId && detail.todoId !== todoId) return;
      (onIngestionComplete ?? onRefreshRelatedDocs)();
    };

    const handleDocumentMutation = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      const path = documentPathFromMutationDetail(detail);
      if (!path) return;
      if (extractTodoIdFromPath(path) === todoId) {
        onRefreshRelatedDocs();
      }
    };

    window.addEventListener(BASECAMP_TODO_REFRESH, handleTodoRefresh);
    window.addEventListener(BASECAMP_INGESTION_COMPLETE, handleIngestionComplete);
    window.addEventListener('document-created', handleDocumentMutation);
    window.addEventListener('document-moved', handleDocumentMutation);

    return () => {
      window.removeEventListener(BASECAMP_TODO_REFRESH, handleTodoRefresh);
      window.removeEventListener(BASECAMP_INGESTION_COMPLETE, handleIngestionComplete);
      window.removeEventListener('document-created', handleDocumentMutation);
      window.removeEventListener('document-moved', handleDocumentMutation);
    };
  }, [todoId, onRefreshRelatedDocs, onFullRefresh, onIngestionComplete]);
}
